import { ChangeEvent, useEffect, useState } from 'react';

// material-ui
import {
    Box,
    Checkbox,
    Collapse,
    Divider,
    Fade,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Switch,
    TextField
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { useIntl } from 'react-intl';

// project imports
import { useSelector, dispatch } from 'store';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

// types
import { getApprovalProfiles, getProfiles, getVendors, registerUserSuccess, updateUserInfo, updateNumber } from 'store/slices/security';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import useAuth from 'hooks/useAuth';

import SaveIcon from '@mui/icons-material/Save';
import { NewUserType } from 'types/components/security';
import LoadingButton from '@mui/lab/LoadingButton';
import { expToken } from 'utils/helpers';

export default function UserForm({
    /* handleChange, */
    user,
    handleSuccess,
    handleCancel
}: {
    user?: NewUserType;
    handleChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    handleSelectChange?: (e: SelectChangeEvent) => void;
    handleCancel: () => void;
    handleSuccess: (mode: 'CREATE' | 'EDIT') => void;
}) {
    // hooks
    const theme = useTheme();
    const intl = useIntl();
    const { logout } = useAuth();

    type typeFormValues = {
        firstName: string;
        lastName: string;
        email: string;
        pass: string;
        phoneNumber: string | number;
        employeNumber: any;
        profileId: any;
        idApprovalProfile: string | number;
        providerId: any;
        status: number;
    };

    const [isMFA, setIsMFA] = useState(false);
    const [isFederated, setIsFederated] = useState(false);
    // store
    const { profiles, vendors, approvalProfiles, loadingEditInfo } = useSelector((state) => state.user);
    const { fatherMerchant, tenantId } = useSelector((state) => state.auth);
    const [initialFormValues] = useState<typeFormValues>({
        firstName: '',
        lastName: '',
        email: '',
        pass: '',
        phoneNumber: '',
        employeNumber: '',
        profileId: '',
        idApprovalProfile: '',
        providerId: '',
        status: 0
    });

    useEffect(() => {
        if (user?.phoneNumber) {
            setIsMFA(true);
        }
        if (!user?.phoneNumber) {
            setIsMFA(false);
        }
    }, [user]);

    useEffect(() => {
        if (!isMFA) {
            values.phoneNumber = '';
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isMFA]);

    useEffect(() => {
        if (!fatherMerchant) return;
        dispatch(getProfiles({ idMerchant: fatherMerchant.merchantId }));
        dispatch(getVendors(fatherMerchant.merchantId));
        dispatch(getApprovalProfiles(fatherMerchant.merchantId));
    }, [fatherMerchant]);

    const tokenExpired = () => {
        const serviceToken = window.localStorage.getItem('serviceToken');

        if (serviceToken) {
            const expirationToken = expToken(serviceToken);
            const dateNow = Date.now();
            if (expirationToken <= dateNow) {
                logout();
            }
        } else {
            logout();
        }
    };

    const phoneRegExp = /^\+\d{2,3}\d{7,10}$/;

    const formik = useFormik({
        initialValues: initialFormValues,
        onSubmit: async (values, { resetForm }) => {
            const userInfo = {
                id: user?.id || 0,
                email: values.email,
                name: '',
                phoneNumber: values.phoneNumber,
                firstName: values.firstName,
                lastName: values.lastName,
                status: values.status,
                user: values.email,
                avatar: '',
                idApprovalProfile: values.idApprovalProfile,
                profileId: values.profileId,
                providerId: values.providerId,
                profile: {
                    id: values.profileId
                },
                provider: {
                    idProvider: values.providerId
                }
            };

            if (!fatherMerchant) return;

            const dataSend = {
                ...userInfo,
                profile: { id: userInfo.profileId ?? '' },
                provider: { idProvider: userInfo.providerId ?? '' },
                pass: null,
                idMerchant: fatherMerchant.merchantId
            };

            if (user?.id === 0) {
                await dispatch(
                    registerUserSuccess({
                        email: values.email,
                        password: '.',
                        userProps: dataSend,
                        token: localStorage.getItem('serviceToken') || '',
                        idMerchant: fatherMerchant.merchantId,
                        tenantId: tenantId || '',
                        isMFA,
                        isFederated,
                        status: values.status
                    })
                );
                handleSuccess('CREATE');
            } else {
                await dispatch(updateUserInfo(dataSend));
                await dispatch(
                    updateNumber({
                        email: values.email,
                        password: '.',
                        userProps: dataSend,
                        token: localStorage.getItem('serviceToken') || '',
                        idMerchant: fatherMerchant.merchantId,
                        tenantId: tenantId || '',
                        isMFA,
                        disabled: !Boolean(values.status)
                    })
                );
                handleSuccess('EDIT');
                handleCancel();
            }
            resetForm();
        },
        validationSchema: Yup.object({
            firstName: Yup.string().required(`${intl.formatMessage({ id: 'name_required' })}`),
            lastName: Yup.string().required(`${intl.formatMessage({ id: 'lastName_required' })}`),
            email: Yup.string()
                .required(`${intl.formatMessage({ id: 'email_required' })}`)
                .email(`${intl.formatMessage({ id: 'email_format' })}`)
                .when('id', {
                    is: () => (user?.id ? user?.id > 0 : false),
                    then: Yup.string(),
                    otherwise: Yup.string().required(`${intl.formatMessage({ id: 'email_required' })}`)
                }),
            pass: Yup.string(),
            phoneNumber: Yup.string().matches(phoneRegExp, `${intl.formatMessage({ id: 'phone_invalid' })}`),
            profileId: Yup.string().required(`${intl.formatMessage({ id: 'profile_required' })}`),
            idApprovalProfile: Yup.string().required(`${intl.formatMessage({ id: 'idapproval' })}`)
        })
    });

    const { getFieldProps, errors, touched, handleSubmit, isSubmitting, handleChange, values, handleBlur } = formik;

    useEffect(() => {
        if (user) {
            formik.resetForm({
                values: {
                    firstName: user.firstName || '',
                    lastName: user.lastName || '',
                    email: user.email || '',
                    pass: user.pass || '',
                    phoneNumber: user.phoneNumber || '',
                    employeNumber: user.employeNumber || '',
                    profileId: user.profileId || '',
                    idApprovalProfile: user.idApprovalProfile || '',
                    providerId: user.providerId || '',
                    status: user.status || 0
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    return (
        <form style={{ marginTop: '10px' }} autoComplete="off" onSubmit={handleSubmit}>
            <FormGroup>
                <FormControlLabel
                    labelPlacement="start"
                    control={
                        <Switch
                            onChange={(e) => {
                                handleChange({ target: { name: 'status', value: e.target.checked ? 1 : 0 } });
                            }}
                            checked={Boolean(values?.status)}
                            name="status"
                        />
                    }
                    label={intl.formatMessage({
                        id: 'active'
                    })}
                />
            </FormGroup>
            <Grid container spacing={2}>
                <Grid item {...gridInputProps}>
                    <TextField
                        fullWidth
                        label={intl.formatMessage({ id: 'first_name' })}
                        onClick={tokenExpired}
                        {...getFieldProps('firstName')}
                        error={touched.firstName && Boolean(errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                    />
                </Grid>
                <Grid item {...gridInputProps}>
                    <TextField
                        fullWidth
                        label={intl.formatMessage({ id: 'last_name' })}
                        onClick={tokenExpired}
                        {...getFieldProps('lastName')}
                        error={touched.lastName && Boolean(errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                    />
                </Grid>
                <Grid item {...gridInputProps}>
                    <TextField
                        fullWidth
                        label={intl.formatMessage({ id: 'email' })}
                        onClick={tokenExpired}
                        {...getFieldProps('email')}
                        error={touched.email && Boolean(errors.email)}
                        helperText={touched.email && errors.email}
                        disabled={user?.id ? user?.id > 0 : false}
                        autoComplete="new-password"
                    />
                </Grid>

                <Grid item {...gridInputProps}>
                    <Fade in={!loadingEditInfo}>
                        <FormControl fullWidth>
                            <InputLabel id="select-profile-id">{intl.formatMessage({ id: 'profile' })}</InputLabel>
                            <Select
                                labelId="select-profile-id"
                                id="select-profile"
                                label={intl.formatMessage({ id: 'profile' })}
                                fullWidth
                                onClick={tokenExpired}
                                {...getFieldProps('profileId')}
                                error={touched.profileId && Boolean(errors.profileId)}
                            >
                                {profiles &&
                                    profiles.map(({ id, type }) => (
                                        <MenuItem key={`profile-${id}`} value={id}>
                                            {type}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Fade>
                    {touched.profileId && errors.profileId && (
                        <FormControl fullWidth>
                            <FormHelperText error>{typeof errors.profileId === 'string' ? errors.profileId : undefined}</FormHelperText>
                        </FormControl>
                    )}
                </Grid>

                <Grid item {...gridInputProps}>
                    <Fade in={!loadingEditInfo}>
                        <FormControl fullWidth>
                            <InputLabel id="select-provider-id">{intl.formatMessage({ id: 'vendor' })}</InputLabel>
                            <Select
                                labelId="select-vendor-id"
                                id="select-provider"
                                label={intl.formatMessage({ id: 'vendor' })}
                                fullWidth
                                onClick={tokenExpired}
                                {...getFieldProps('providerId')}
                            >
                                <MenuItem key={`provider`} value="" disabled={!values.providerId}>
                                    {intl.formatMessage({ id: 'no_vendor' })}
                                </MenuItem>
                                {vendors &&
                                    vendors.map(({ idProvider, name }) => (
                                        <MenuItem key={`provider-${idProvider}`} value={idProvider}>
                                            {name}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Fade>
                </Grid>

                <Grid item {...gridInputProps}>
                    <Fade in={!loadingEditInfo}>
                        <FormControl fullWidth>
                            <InputLabel id="select-aproval-profile-id">{intl.formatMessage({ id: 'approval_profile' })}</InputLabel>
                            <Select
                                labelId="select-aproval-profile-id"
                                id="select-aproval-profile"
                                label={intl.formatMessage({ id: 'approval_profile' })}
                                fullWidth
                                onClick={tokenExpired}
                                {...getFieldProps('idApprovalProfile')}
                                error={touched.idApprovalProfile && Boolean(errors.idApprovalProfile)}
                            >
                                {approvalProfiles &&
                                    approvalProfiles.map(({ idProfile, profileName }) => (
                                        <MenuItem key={`aproval-provider-${idProfile}`} value={idProfile}>
                                            {profileName}
                                        </MenuItem>
                                    ))}
                            </Select>
                        </FormControl>
                    </Fade>
                    {touched.idApprovalProfile && errors.idApprovalProfile && (
                        <FormControl fullWidth>
                            <FormHelperText error>{errors.idApprovalProfile}</FormHelperText>
                        </FormControl>
                    )}
                </Grid>

                <Grid item {...gridInputProps}>
                    <FormGroup>
                        {!user?.id && (
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isFederated}
                                        onChange={(e) => {
                                            setIsFederated(e.target.checked);
                                            !e.target.checked && setIsMFA(false);
                                        }}
                                    />
                                }
                                label={intl.formatMessage({ id: 'is_federated' })}
                            />
                        )}
                        <Collapse in={!isFederated}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={isMFA}
                                        onChange={(e) => {
                                            setIsMFA(e.target.checked);
                                        }}
                                    />
                                }
                                label={intl.formatMessage({ id: 'enable_mfa' })}
                            />
                        </Collapse>
                    </FormGroup>
                </Grid>

                <Grid
                    item
                    {...gridInputProps}
                    sx={{
                        '& .react-tel-input .form-control': {
                            position: 'relative',
                            fontSize: '14px',
                            letterSpacing: '.01rem',
                            marginTop: '0 !important',
                            marginBottom: '0 !important',
                            paddingLeft: '48px',
                            marginLeft: 0,
                            background: theme.palette.mode === 'dark' ? '#1a223f' : '',
                            border: theme.palette.mode === 'dark' ? '1px solid #1a223f' : '',
                            borderRadius: '8px',
                            lineHeight: '25px',
                            height: '48px',
                            width: '214px',
                            outline: 'none',
                            color: theme.palette.mode === 'dark' ? '#bdc8f0' : ''
                        },
                        '& .react-tel-input .flag-dropdown': {
                            position: 'absolute',
                            top: 0,
                            bottom: 0,
                            padding: 0,
                            backgroundColor: theme.palette.mode === 'dark' ? '#1a223f' : '',
                            border: theme.palette.mode === 'dark' ? '1px solid #1a223f' : '',
                            borderRadius: '8px 0 0 8px'
                        },
                        '& .react-tel-input .flag-dropdown.open .selected-flag': {
                            background: theme.palette.mode === 'dark' ? '#1a223f' : '',
                            borderRadius: '8px 0 0 0'
                        },
                        '& .react-tel-input .selected-flag:hover, .react-tel-input .selected-flag:focus': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#1a223f' : ''
                        },
                        '& .react-tel-input .country-list': {
                            outline: 'none',
                            zIndex: 1,
                            listStyle: 'none',
                            position: 'absolute',
                            padding: 0,
                            margin: '10px 0 10px -1px',
                            boxShadow: '1px 2px 10px rgba(0,0,0,0.35)',
                            backgroundColor: theme.palette.mode === 'dark' ? '#1a223f' : '',
                            width: '300px',
                            maxHeight: '200px',
                            overflowY: 'scroll',
                            borderRadius: '0 0 8px 8px',
                            color: theme.palette.mode === 'dark' ? '#bdc8f0' : ''
                        },
                        '& .react-tel-input .country-list .country:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#232d53' : ''
                        },
                        '& .react-tel-input .country-list .country.highlight': {
                            backgroundColor: theme.palette.mode === 'dark' ? '#26325e' : ''
                        },
                        '& ::placeholder': {
                            color: theme.palette.mode === 'dark' ? '#8492c4' : ''
                        },
                        '& .react-tel-input:hover': {
                            border: `${touched.phoneNumber && errors.phoneNumber ? '1px solid red' : '1px solid #bdc8f0'}`,
                            borderRadius: '8px'
                        },
                        '& .react-tel-input': {
                            border: `${touched.phoneNumber && errors.phoneNumber ? '1px solid red' : ''}`,
                            borderRadius: `${touched.phoneNumber && Boolean(errors.phoneNumber) ? '8px' : ''}`
                        }
                    }}
                >
                    <Collapse in={isMFA}>
                        <PhoneInput
                            inputProps={{
                                name: 'phoneNumber',
                                id: 'phoneNumber'
                            }}
                            value={values.phoneNumber.toString()}
                            onClick={tokenExpired}
                            onBlur={handleBlur}
                            onChange={(data, _, e) => {
                                e.target.value = e?.target?.value && e.target.value.replace(/[^+\d]+/g, '');
                                handleChange(e);
                            }}
                        />
                        {touched.phoneNumber && errors.phoneNumber && (
                            <FormControl fullWidth>
                                <FormHelperText error>{errors.phoneNumber}</FormHelperText>
                            </FormControl>
                        )}
                    </Collapse>
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ mb: 1 }} />
                </Grid>
                <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <LoadingButton
                            sx={{ paddingLeft: '5rem', paddingRight: '5rem' }}
                            variant="outlined"
                            startIcon={<SaveIcon />}
                            type="submit"
                            onClick={tokenExpired}
                            loadingPosition="start"
                            loading={isSubmitting}
                            disabled={!tenantId}
                        >
                            <span>{intl.formatMessage({ id: 'save' })}</span>
                        </LoadingButton>
                    </Box>
                </Grid>
            </Grid>
        </form>
    );
}

const gridInputProps = {
    xs: 12,
    sm: 6,
    xl: 6
};

export const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    maxWidth: 600,
    bgcolor: 'background.paper',
    boxShadow: 24,
    pt: 2,
    px: 3,
    pb: 3,
    borderRadius: 2
};
